// src/locales/English/en.termsOfUse.ts

const termsOfUse = {
  termsOfUse: {
    meta: {
      title: "{{appName}} — Terms of Use",
      closeAria: "Close",
      closeTitle: "Close",
      lastUpdated: "Last updated: {{date}}",
      top: "↑ Top",
      print: "Print / Save as PDF",
      contact: "Contact",
      copyright: "© {{year}} {{companyName}}. All rights reserved.",
    },

    nav: {
      introduction: "Introduction",
      eligibility: "Eligibility",
      account: "Account",
      service: "Service",
      content: "User Content",
      music: "Music & Audio",
      acceptableUse: "Acceptable Use",
      community: "Community Standards",
      ip: "Intellectual Property",
      copyright: "Copyright",
      thirdParty: "Third-Party Services",
      termination: "Termination",
      availability: "Availability",
      liability: "Liability",
      indemnity: "Indemnity",
      changes: "Changes",
      law: "Governing Law",
      contact: "Contact",
    },

introduction: {
  title: "1. General",

  welcome: "Welcome to {{appName}}!",

  body:
   "{{appName}} is a social media platform that enables users to discover content, share experiences, connect with others, and communicate through features provided by the Service. In these Terms, 'Service' refers to {{appName}}, including its website, applications, content-sharing functionality, messaging features, location-based features, and related services. These Terms of Use govern your access to and use of the Service. By creating an account, accessing, or using the Service, you agree to these Terms. If you do not agree, you must not use the Service."
},

    eligibility: {
title: "2. Eligibility",
bullets: [
"You must be at least **18 years old** to use {{appName}}.",
"You must be legally permitted to enter into and comply with these Terms.",
"You must comply with all applicable laws and regulations.",
"You may not use {{appName}} if your account has previously been suspended or terminated by us.",
],
},


    account: {
title: "3. Your Account",
body:
"Your account is personal to you. You are responsible for maintaining the security of your account and for activity that occurs through it.",

bullets: [
"Keep your login credentials secure and confidential.",
"Do not share, sell, transfer, or allow others to use your account.",
"Notify us if you believe your account has been compromised.",
"Use your account in accordance with these Terms and applicable laws.",
"We may require additional information or verification when necessary to protect users, the Service, or comply with legal obligations."
],
},

service: {
title: "4. Description of the Service",
body:
"{{appName}} is a social platform that enables users to create profiles, share content, discover public posts, interact with other users, communicate through private messaging features, and access location-based functionality.",

bullets: [
"**Profiles**: Users may create and maintain a profile.",
"**Content sharing**: Users may upload and share photos, videos, and other content made available through the Service.",
"**Discovery**: Users may discover public content shared by others.",
"**Location-based features**: Location information is used in connection with content sharing, discovery features, nearby content, and location labels associated with posts. Creating a post requires location information to determine location labels and support location-based features and content discovery within the Service. If location permissions are not granted, posting content may not be available.",
"**Content categorization**: Content may be categorized and discovered through location labels, hidden hashtags, music associations, and similar organizational features available within the Service.",
"**Social interactions**: Users may follow others, like content, block users, report users or content, and receive notifications.",
"**Messaging**: Users may communicate through private messaging features.",
"Features, functionality, and availability of the Service may be modified, limited, suspended, or discontinued at any time."
],
},


content: {
title: "5. User Content",
body:
"You retain ownership of the content you create and upload to {{appName}}.",

bullets: [
"By posting content, you grant {{companyName}} a worldwide, non-exclusive, royalty-free license to host, store, display, distribute, reproduce, process, and make that content available as necessary to operate, provide, improve, protect, and support the Service.",
"You confirm that you have the necessary rights, permissions, and authority to upload, share, and make your content available through the Service.",
"You are responsible for ensuring that your content does not violate applicable law or the rights of any person or entity.",
"Public content may be visible to other users and may be associated with your profile and location labels where applicable."
],

licenseEnd:
"This license ends when your content is deleted, except to the extent that continued retention is necessary for legal compliance, security, moderation, backup, dispute resolution, or technical purposes.",
},

music: {
title: "6. Music and Audio Content",

body:
"{{appName}} may provide access to music, audio tracks, sound effects, or other audio content that can be used in connection with features of the Service.",

bullets: [
"Music and audio content made available through the Service may be owned by {{companyName}} or licensed from third parties.",
"Your use of music and audio content is limited to use within the Service and in accordance with these Terms.",
"Users are responsible for ensuring that any audio content they upload or make available through the Service does not infringe the rights of third parties.",
"Availability of specific music or audio content may vary by region, feature, licensing restrictions, or time period.",
"We may add, modify, restrict, or remove music and audio content at any time."
],
},


acceptableUse: {
title: "7. Acceptable Use",
bullets: [
"Do not violate applicable laws or regulations.",
"Do not infringe intellectual property, privacy, publicity, or other rights.",
"Do not harass, threaten, abuse, intimidate, stalk, or exploit others.",
"Do not post hateful, violent, extremist, terrorist, sexually exploitative, or otherwise harmful content.",
"Do not post fraudulent, deceptive, misleading, scam-related, or impersonating content.",
"Do not impersonate another person, company, organization, brand, or public figure.",
"Do not use profile names, trademarks, or brand names in a way that falsely suggests affiliation, endorsement, or official representation.",
"Do not share another person's private or sensitive information without permission.",
"Do not distribute malware, harmful code, spam, or automated abuse.",
"Do not scrape, crawl, copy, or extract data from the Service without permission.",
"Do not bypass, interfere with, reverse engineer, or attack the Service, security systems, rate limits, or technical safeguards.",
"Do not use bots, automated tools, fake engagement, or coordinated manipulation without our permission.",
"Do not copy, extract, download, distribute, sell, or use music or audio content outside the Service except where expressly permitted.",
"Political advertising, political campaigning, or coordinated political messaging is not permitted."
],
},

community: {
title: "8. Community Standards",
body:
"Users are expected to contribute to a safe, respectful, and trustworthy community. In addition to these Terms, users must comply with the {{appName}} Community Guidelines and any policies that apply to specific features of the Service.",

bullets: [
"We may review, remove, restrict, or disable content that violates these Terms, our Community Guidelines, applicable law, or the safety and integrity of the Service.",
"We may limit visibility of content, restrict access to features, issue warnings, suspend accounts, or terminate accounts where appropriate.",
"We may take action based on user reports, automated systems, internal reviews, legal requirements, or other information available to us.",
"Enforcement decisions are made at our reasonable discretion and may take into account the severity, frequency, and context of violations.",
"Repeated, serious, or deliberate violations may result in permanent account restrictions or termination."
],
},


ip: {
title: "9. Intellectual Property",
body:
"{{appName}}, including its software, technology, design, interface, branding, logos, trademarks, features, music and audio content, and other materials made available through the Service, is owned by {{companyName}} or its licensors and is protected by applicable intellectual property laws. Except as expressly permitted by these Terms, you may not copy, modify, distribute, sell, reverse engineer, or create derivative works from any part of the Service. These Terms do not grant you ownership of the Service or any intellectual property rights belonging to {{companyName}} or its licensors.",
},


copyright: {
title: "10. Copyright Complaints",
body:
"We respect intellectual property rights and expect users to do the same. If you believe that content available through {{appName}} infringes your copyright or other intellectual property rights, you may submit a notice to us. We may investigate the claim, remove or restrict access to the content, and take action against repeat infringers where appropriate.",

agent:
"{{companyName}}\n{{legalAddress}}\nEmail: {{dmcaEmail}}",
},

thirdParty: {
title: "11. Third-Party Services",
body:
"{{appName}} relies on third-party providers and infrastructure services to operate, secure, host, store, deliver, and improve the Service. These providers may process data on our behalf in accordance with applicable agreements and legal requirements.",

bullets: [
"Third-party providers may include hosting, cloud infrastructure, content delivery, storage, security, communications, analytics, location, and other technology providers.",
"The availability and performance of certain features may depend on third-party services outside our reasonable control.",
"We are not responsible for the content, policies, practices, or availability of third-party websites, services, or platforms that are not operated by {{companyName}}.",
"Use of third-party services may be subject to the terms and policies of those third parties."
],
},

termination: {
title: "12. Account Suspension, Deactivation and Termination",

body:
"You may stop using {{appName}} at any time. If you no longer wish to use the Service, you may deactivate or delete your account through the available account settings. We may suspend, restrict, disable, or terminate your account or access to the Service where reasonably necessary.",

bullets: [
"If you violate these Terms.",
"If you violate our Community Guidelines or other applicable policies.",
"If required by law, legal process, or regulatory obligations.",
"If your activity reasonably creates risk for users, {{companyName}}, third parties, or the Service.",
"If your activity reasonably threatens the safety, security, integrity, or operation of the Service.",
"If we reasonably believe your account has been compromised, used fraudulently, or involved in abuse of the Service.",
"If you provide false, misleading, or inaccurate information during registration or account use."
],

effect:
"Suspension, restriction, deactivation, or termination may result in loss of access to your account, profile, content, messages, followers, settings, purchased features, and other Service functionality. Certain information may continue to be retained where required for legal, security, fraud prevention, dispute resolution, backup, operational, or technical purposes."
},

availability: {
title: "13. Service Availability",

body:
"We strive to provide a reliable and secure Service, but we do not guarantee that {{appName}} will always be available, uninterrupted, secure, error-free, or free from delays, data loss, or service disruptions. Features may be modified, suspended, restricted, or discontinued at any time.",

bullets: [
"The availability and performance of the Service may be affected by maintenance, updates, technical issues, third-party providers, network interruptions, security incidents, or events beyond our reasonable control.",
"We do not guarantee the preservation, availability, delivery, or recovery of any content, messages, media, account data, or other information.",
"Users are responsible for maintaining their own copies of content they consider important.",
"To the maximum extent permitted by law, {{companyName}} is not responsible for losses resulting from outages, interruptions, delays, data corruption, data loss, backup failures, or technical errors."
],
},

liability: {
title: "14. Disclaimers and Limitation of Liability",

body:
'The Service is provided on an "as is" and "as available" basis. To the maximum extent permitted by applicable law, {{companyName}} disclaims all warranties, whether express, implied, statutory, or otherwise, including warranties of merchantability, fitness for a particular purpose, non-infringement, availability, security, accuracy, reliability, or uninterrupted operation.',

bullets: [
"{{companyName}} is not liable for indirect, incidental, special, consequential, exemplary, punitive, or similar damages.",
"{{companyName}} is not responsible for loss of data, messages, content, media, profits, revenue, goodwill, reputation, business opportunities, or other intangible losses.",
"{{companyName}} is not responsible for outages, interruptions, delays, service disruptions, data corruption, data loss, backup failures, unauthorized access, security incidents, or failures caused by third-party providers.",
"{{companyName}} is not responsible for content, conduct, statements, actions, or omissions of users or third parties.",
"To the maximum extent permitted by law, our total aggregate liability arising out of or relating to the Service shall not exceed USD 100."
],
},

indemnity: {
title: "15. Indemnification",

body:
"To the extent permitted by applicable law, you agree to indemnify and hold harmless {{companyName}}, its affiliates, officers, directors, employees, contractors, partners, licensors, and service providers from and against claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising from or relating to:",

bullets: [
"Your use of the Service.",
"Your content, posts, messages, media, or other material you submit through the Service.",
"Your violation of these Terms, our Community Guidelines, or other applicable policies.",
"Your violation of applicable law or regulations.",
"Your violation of the rights of another person, organization, or entity, including intellectual property, privacy, publicity, or other legal rights.",
"Any claim arising from your actions, conduct, or misuse of the Service."
],
},

changes: {
title: "16. Changes to the Service and Terms",

body:
"We may modify, update, improve, restrict, suspend, discontinue, or otherwise change the Service or these Terms from time to time. If we make material changes, we may provide notice through the Service, by email, or by other reasonable means.",

notice:
"Changes become effective when posted or on the date specified in the notice, unless otherwise required by applicable law.",

effect:
"By continuing to access or use {{appName}} after changes take effect, you agree to the updated Terms. If you do not agree to the changes, you must stop using the Service."
},

law: {
title: "17. Governing Law",

body:
"These Terms and any dispute arising out of or relating to the Service shall be governed by and construed in accordance with the laws of the State of Florida, United States, without regard to conflict-of-law principles. Nothing in these Terms limits or excludes any mandatory consumer rights or protections that apply under the laws of your country of residence where such rights cannot be waived by agreement.",
},

    contact: {
      title: "18. Contact",
      body:
        "{{companyName}}\n{{companyAddress}}\n{{contactEmail}}",
    },
  },
} as const;

export default termsOfUse;